import React, { Component } from "react";
import * as d3 from "d3";

interface State {
  tooltipContent: string;
  tooltipTop: string;
  tooltipLeft: string;
  tooltipVisible: boolean;
}

interface Props {
  ionoData: any;
  stationInfo: any;
  stationId: string;
  constellation: string;
  handleOpenMapPanel: (d: any) => void;
}

const COLORS = {
  ocean: "#f0f7ff",
  land: "#e2e8f0",
  landStroke: "#cbd5e1",
  graticule: "#e2e8f0",
  stationFill: "#0ea5e9",
  stationStroke: "#fff",
  stationPulse: "#0ea5e9",
  labelFill: "#334155",
};

class MapD3 extends Component<Props, State> {
  private svgRef!: SVGSVGElement;
  private projection: any;
  private path: any;
  private mapGroup: any;
  private contentGroup: any;   // satellite tracks
  private stationGroup: any;   // station marker (always on top)
  private zoomBehavior: any;
  private mapLoaded = false;

  constructor(props: any) {
    super(props);
    this.state = {
      tooltipContent: "",
      tooltipTop: "0px",
      tooltipLeft: "0px",
      tooltipVisible: false,
    };
  }

  private showTooltip(evt: MouseEvent, content: string) {
    this.setState({
      tooltipVisible: true,
      tooltipContent: content,
      tooltipLeft: evt.offsetX + 15 + "px",
      tooltipTop: evt.offsetY - 10 + "px",
    });
  }

  private hideTooltip() {
    this.setState({ tooltipVisible: false });
  }

  private static readonly VB_WIDTH = 960;
  private static readonly VB_HEIGHT = 500;

  private initMap() {
    const svg = d3.select(this.svgRef);
    svg.selectAll("*").remove();

    const W = MapD3.VB_WIDTH;
    const H = MapD3.VB_HEIGHT;

    this.projection = d3
      .geoNaturalEarth1()
      .fitSize([W - 40, H - 20], { type: "Sphere" } as any)
      .translate([W / 2, H / 2]);
    this.path = d3.geoPath().projection(this.projection);

    const rootG = svg.append("g").attr("class", "map-root");

    // Ocean
    rootG
      .append("path")
      .datum({ type: "Sphere" } as any)
      .attr("d", this.path as any)
      .attr("fill", COLORS.ocean)
      .attr("stroke", COLORS.landStroke)
      .attr("stroke-width", 0.5);

    // Graticule
    rootG
      .append("path")
      .datum(d3.geoGraticule10())
      .attr("d", this.path as any)
      .attr("fill", "none")
      .attr("stroke", COLORS.graticule)
      .attr("stroke-width", 0.3);

    this.mapGroup = rootG.append("g").attr("class", "map-layer");
    // Tracks below, station on top — so marker is always clickable
    this.contentGroup = rootG.append("g").attr("class", "tracks-layer");
    this.stationGroup = rootG.append("g").attr("class", "station-layer");

    this.zoomBehavior = d3
      .zoom()
      .scaleExtent([1, 20])
      .translateExtent([[0, 0], [W, H]])
      .on("zoom", (evt: any) => {
        rootG.attr("transform", evt.transform);
      });
    svg.call(this.zoomBehavior);

    d3.json("/world.geojson").then((worldData: any) => {
      this.mapGroup
        .selectAll("path")
        .data(worldData.features)
        .enter()
        .append("path")
        .attr("d", (d: any) => this.path(d))
        .attr("fill", COLORS.land)
        .attr("stroke", COLORS.landStroke)
        .attr("stroke-width", 0.4);

      this.mapLoaded = true;
      this.drawContent();
    });
  }

  private drawContent() {
    if (!this.mapLoaded || !this.contentGroup) return;

    this.contentGroup.selectAll("*").remove();
    this.stationGroup.selectAll("*").remove();

    const { ionoData, stationInfo, stationId, constellation } = this.props;
    const svg = d3.select(this.svgRef);

    // --- Satellite tracks (drawn FIRST, below station) ---
    if (ionoData) {
      const satellites = Object.keys(ionoData).filter(
        (k) => ionoData[k] && ionoData[k].length > 1
      );

      if (satellites.length > 0) {
        const colorScale = d3.scaleOrdinal(d3.schemeTableau10).domain(satellites);
        const lineGen = d3.line<[number, number]>().x(d => d[0]).y(d => d[1]);

        for (const sat of satellites) {
          const points: any[] = ionoData[sat];
          if (!points || points.length < 2) continue;

          const color = colorScale(sat);
          const satLabel =
            constellation.charAt(0) +
            (parseInt(sat) < 10 ? "0" + sat : sat);

          // Project points to screen space, downsample to avoid overdraw.
          // Data is newest-first — reverse so path goes tail→head.
          const reversed = [...points].reverse();
          const projected: [number, number][] = [];
          let prevX = -Infinity, prevY = -Infinity;
          for (const p of reversed) {
            const pt = this.projection([p.lon, p.lat]);
            if (!pt) continue;
            // Skip points closer than 0.5px to previous (reduces DOM load)
            const dx = pt[0] - prevX, dy = pt[1] - prevY;
            if (dx * dx + dy * dy < 0.25) continue;
            projected.push(pt as [number, number]);
            prevX = pt[0]; prevY = pt[1];
          }
          if (projected.length < 2) continue;

          // Single <path> per satellite — thin semi-transparent line
          this.contentGroup
            .append("path")
            .attr("d", lineGen(projected))
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", 1.5)
            .attr("stroke-linecap", "round")
            .attr("stroke-linejoin", "round")
            .attr("opacity", 0.55)
            .style("cursor", "pointer")
            .on("mousemove", (event: MouseEvent) => this.showTooltip(event, satLabel))
            .on("mouseout", () => this.hideTooltip());
        }
      }
    }

    // --- Station marker (drawn LAST, always on top) ---
    if (stationInfo?.latLng) {
      const coord: [number, number] = [stationInfo.latLng[1], stationInfo.latLng[0]];
      const pt = this.projection(coord);
      if (pt) {
        const openPanel = () => this.props.handleOpenMapPanel(null);

        // Pulse ring
        this.stationGroup
          .append("circle")
          .attr("cx", pt[0])
          .attr("cy", pt[1])
          .attr("r", 8)
          .attr("fill", "none")
          .attr("stroke", COLORS.stationPulse)
          .attr("stroke-width", 1.5)
          .attr("opacity", 0.5)
          .attr("class", "station-pulse");

        // Clickable hit area (larger invisible circle for easier clicking)
        this.stationGroup
          .append("circle")
          .attr("cx", pt[0])
          .attr("cy", pt[1])
          .attr("r", 12)
          .attr("fill", "transparent")
          .style("cursor", "pointer")
          .on("click", openPanel);

        // Station dot
        this.stationGroup
          .append("circle")
          .attr("cx", pt[0])
          .attr("cy", pt[1])
          .attr("r", 4)
          .attr("fill", COLORS.stationFill)
          .attr("stroke", COLORS.stationStroke)
          .attr("stroke-width", 1.5)
          .style("cursor", "pointer")
          .on("mousemove", (event: MouseEvent) =>
            this.showTooltip(event, stationId.toUpperCase())
          )
          .on("mouseout", () => this.hideTooltip())
          .on("click", openPanel);

        // Label (clickable)
        this.stationGroup
          .append("text")
          .attr("x", pt[0] + 7)
          .attr("y", pt[1] + 4)
          .attr("font-size", "11px")
          .attr("font-weight", "600")
          .attr("fill", COLORS.labelFill)
          .style("cursor", "pointer")
          .on("click", openPanel)
          .on("mousemove", (event: MouseEvent) =>
            this.showTooltip(event, "Open satellite panel")
          )
          .on("mouseout", () => this.hideTooltip())
          .text(stationId.toUpperCase());
      }
    }
  }

  resetZoom = () => {
    const svg = d3.select(this.svgRef) as any;
    svg
      .transition()
      .duration(500)
      .call(this.zoomBehavior.transform, d3.zoomIdentity);
    svg.select(".map-root").transition().duration(500).attr("transform", null);
  };

  componentDidMount() {
    this.initMap();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.constellation !== this.props.constellation) {
      this.initMap();
      return;
    }
    if (prevProps.ionoData !== this.props.ionoData) {
      this.drawContent();
    }
  }

  render() {
    return (
      <div className="relative">
        {this.state.tooltipVisible && (
          <div
            className="absolute z-50 rounded bg-gray-800 px-2 py-1 text-xs text-white shadow pointer-events-none whitespace-nowrap"
            style={{
              left: this.state.tooltipLeft,
              top: this.state.tooltipTop,
            }}
          >
            {this.state.tooltipContent}
          </div>
        )}
        <div className="relative w-full overflow-hidden">
          <svg
            className="w-full h-auto"
            ref={(ref: SVGSVGElement) => (this.svgRef = ref)}
            viewBox="0 0 960 500"
            preserveAspectRatio="xMidYMid meet"
            style={{ background: COLORS.ocean }}
          />
          <button
            onClick={this.resetZoom}
            className="absolute bottom-3 left-3 rounded-md bg-brand-300 px-3 py-1 text-xs font-semibold uppercase text-white shadow hover:bg-brand-400 transition"
          >
            Reset Zoom
          </button>
        </div>
      </div>
    );
  }
}

export default MapD3;
